import { requestIdFromChannelId } from '@utils/chat';

export interface UnreadState {
  unreadChannelMap: Record<string, number>;
  unreadCount: number | undefined;
}

// The fields of a Stream event that unread tracking needs, with channel_id already normalized.
export interface UnreadEvent {
  channelId: string | undefined;
  isMe: boolean;
  totalUnreadCount: number | undefined;
  unreadMessages: number | undefined;
}

export const sumCounts = (channelMap: Record<string, number>) =>
  Object.values(channelMap).reduce((sum, n) => sum + n, 0);

// Stream's total still counts channels the user can't open, so subtract them; 0 resets everything.
export const applyChannelCount = (
  prev: UnreadState,
  channelId: string | undefined,
  channelCount: number,
  totalUnreadCount: number | undefined,
  hiddenTotal: number
): UnreadState => ({
  unreadChannelMap: totalUnreadCount === 0
    ? {}
    : { ...prev.unreadChannelMap, [channelId as string]: channelCount },
  unreadCount: totalUnreadCount === undefined
    ? prev.unreadCount
    : Math.max(0, totalUnreadCount - hiddenTotal)
});

// message.read carries no total, so the new count is derived from the channel's tracked count.
export const markChannelRead = (prev: UnreadState, channelId: string | undefined): UnreadState => {
  const current = prev.unreadCount ?? 0;
  const unreadCount = current > 0 ? current - (prev.unreadChannelMap[channelId as string] || 0) : 0;
  return {
    unreadChannelMap: unreadCount === 0 ? {} : { ...prev.unreadChannelMap, [channelId as string]: 0 },
    unreadCount
  };
};

// Deletes the channel's key outright (rather than zeroing it) so the badge clears for channels the
// admin has opted out of.
export const removeChannel = (
  prev: UnreadState,
  channelId: string | undefined,
  totalUnreadCount: number | undefined,
  hiddenTotal: number
): UnreadState => {
  const removedCount = prev.unreadChannelMap[channelId as string] ?? 0;
  const unreadCount = totalUnreadCount === undefined
    ? Math.max(0, (prev.unreadCount ?? 0) - removedCount)
    : Math.max(0, totalUnreadCount - hiddenTotal);
  const remaining = { ...prev.unreadChannelMap };
  delete remaining[channelId as string];
  return { unreadChannelMap: unreadCount === 0 ? {} : remaining, unreadCount };
};

// Splits an event's channels out of the visible state; undefined when the event has none tracked.
export const takeEventChannels = (
  prev: UnreadState,
  requestId: string
): { hidden: Record<string, number>; next: UnreadState } | undefined => {
  const entries = Object.entries(prev.unreadChannelMap);
  const hidden = Object.fromEntries(entries.filter(([id]) => requestIdFromChannelId(id) === requestId));
  if (Object.keys(hidden).length === 0) return undefined;

  return {
    hidden,
    next: {
      unreadChannelMap: Object.fromEntries(entries.filter(([id]) => !(id in hidden))),
      unreadCount: Math.max(0, (prev.unreadCount ?? 0) - sumCounts(hidden))
    }
  };
};
